import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { IconButton, MenuItem, Select } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid } from 'react-data-grid';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import ImportExcel from '../../Components/ImportExcel';
import ExportExcel from '../../Components/ExportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  handleCopy,
  renderTextEditor,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';

function getComparator(sortColumn) {
  switch (sortColumn) {
    case 'AccountTypeCode':
      return (a, b) => {
        return a[sortColumn]?.localeCompare(b[sortColumn]);
      };
    case 'AccountGLType':
      return (a, b) => {
        return a[sortColumn]?.localeCompare(b[sortColumn]);
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

function AccountTypeTable({
  name,
  rows,
  onClickedAdd,
  onClickedDelete,
  getStateData,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    AccountTypeCode: '',
    AccountGLType: '',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [sortColumns, setSortColumns] = useState([]);
  
  useEffect(() => {
    requestAnimationFrame(function () {
      const event = new UIEvent('resize', {
        bubbles: true,
        cancelable: false,
        view: window
      });
      window.dispatchEvent(event);
    });
  }, []);

  useEffect(() => {
    setTableRows(
      rows.sort((a, b) => (a.AccountTypeCode < b.AccountTypeCode ? -1 : 1)),
    );

    return () => {
      setTableRows([]);
    };
  }, [rows]);

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    setTableRows(updatedRows);
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleClickDelete = () => {
    if (selectedRow) {
      onClickedDelete(name, [selectedRow.id]);
      setSelectedRow(null);
    }
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleExcelImport = (updated_data) => {
    ManageReportDataApi.importDataFromExcel(name, updated_data)
      .then((res) => {
        if (res.status === 200) {
          getStateData(res.data);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const chartaccountcolumns = useMemo(() => {
    return [
      {
        key: 'AccountTypeCode',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>AccountTypeCode</b>
            {sortColumns.find(
              (column) => column.columnKey === 'AccountTypeCode',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'AccountTypeCode',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.AccountTypeCode}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    AccountTypeCode: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderEditCell: renderTextEditor,
      },
      {
        key: 'AccountGLType',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <b>AccountGLType</b>
            {sortColumns.find(
              (column) => column.columnKey === 'AccountGLType',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'AccountGLType',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.AccountGLType}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    AccountGLType: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.AccountGLType ? props.row.AccountGLType : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    AccountGLType: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              <MenuItem value="BS">BS</MenuItem>
              <MenuItem value="P&L">P&L</MenuItem>
            </Select>
          );
        },
      },
    ];
    // eslint-disable-next-line
  }, [tableRows, sortColumns]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.AccountTypeCode
          ? r.AccountTypeCode?.toLowerCase().includes(
              filters.AccountTypeCode.toLowerCase(),
            )
          : true) &&
        (filters.AccountGLType
          ? r.AccountGLType?.toString().includes(
              filters.AccountGLType.toString(),
            )
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportValue = useMemo(() => {
    let export_value = {
      AccountType: {
        columns: [],
        data: [],
      },
    };
    export_value.AccountType.columns = [{ title: 'id', width: { wpx: 80 } }];
    tableRows.forEach((data) => {
      export_value.AccountType.data.push([
        {
          value: data.id,
          style: { numFmt: '0' },
        },
        { value: data.AccountTypeCode ? data.AccountTypeCode : '' },
        { value: data.AccountGLType ? data.AccountGLType : '' },
      ]);
    });

    return export_value;
  }, [tableRows]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} />
        <ExportExcel filename="AccountType" value={exportValue} />
        <IconButton aria-label="Add" onClick={() => onClickedAdd(name)}>
          <AddIcon />
        </IconButton>
        <IconButton aria-label="Search" onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={chartaccountcolumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 145 }}
        />
      </FilterContext.Provider>
    </>
  );
}

AccountTypeTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
};

export default AccountTypeTable;
